/**
 * Client-side generator for auto-assigned alias usernames.
 *
 * Free aliases cannot pick their own username; they are handed a generated one
 * and may rotate it as often as they like. Generation is deliberately pure and
 * local so rotation costs no server resources — the name is only checked
 * (blocked words, then availability) when the user advances to the next step.
 *
 * {@link isGeneratedUsername} is the server-side counterpart: it proves a
 * submitted name really came from this vocabulary, so a free alias cannot bypass
 * rotation by calling the create endpoint directly.
 *
 * @module utils/username-generator
 */
import { USERNAME_ADJECTIVES, USERNAME_NOUNS } from '../constants/username-wordlists';
/** Lowest generated suffix (keeps the number at two digits, never zero-padded). */
const MIN_SUFFIX = 10;
/** Highest generated suffix (three digits). */
const MAX_SUFFIX = 999;
const NOUNS = new Set(USERNAME_NOUNS);
/** Letters-then-2-or-3-digits, the only shape {@link generateUsername} emits. */
const GENERATED_SHAPE = /^([a-z]+)([0-9]{2,3})$/;
function pick(items, rng) {
    const index = Math.min(items.length - 1, Math.floor(rng() * items.length));
    return items[index];
}
/**
 * A fresh `<adjective><noun><2-or-3-digit-number>` username.
 *
 * `rng` is injectable for deterministic tests; it must behave like
 * `Math.random` (return a value in `[0, 1)`).
 */
export function generateUsername(rng = Math.random) {
    const adjective = pick(USERNAME_ADJECTIVES, rng);
    const noun = pick(USERNAME_NOUNS, rng);
    const span = MAX_SUFFIX - MIN_SUFFIX + 1;
    const suffix = Math.min(MAX_SUFFIX, MIN_SUFFIX + Math.floor(rng() * span));
    return `${adjective}${noun}${suffix}`;
}
/**
 * Splits a generated username into its adjective, noun, and number.
 *
 * Some names admit more than one adjective/noun split; the first valid split in
 * list order wins, matching {@link isGeneratedUsername}. Expects an
 * already-normalized (lowercase) username. Returns `null` when the name is not
 * one {@link generateUsername} could have produced.
 */
export function parseGeneratedUsername(username) {
    const match = GENERATED_SHAPE.exec(username);
    if (!match)
        return null;
    const letters = match[1];
    const suffix = match[2];
    for (const adjective of USERNAME_ADJECTIVES) {
        if (letters.length <= adjective.length)
            continue;
        if (!letters.startsWith(adjective))
            continue;
        const noun = letters.slice(adjective.length);
        if (NOUNS.has(noun))
            return { adjective, noun, suffix };
    }
    return null;
}
/**
 * Whether `username` could have been produced by {@link generateUsername}.
 *
 * Expects an already-normalized (lowercase) username.
 */
export function isGeneratedUsername(username) {
    return parseGeneratedUsername(username) !== null;
}
function titleCase(word) {
    return word.length === 0 ? word : word[0].toUpperCase() + word.slice(1);
}
/**
 * A display-name suggestion derived from a username.
 *
 * Generated names become `"Adjective Noun 42"` so the three units are readable.
 * Everything else is returned unchanged: a paid alias that typed `my_handle`
 * should see that exact string as the default, not a guess at word boundaries.
 */
export function formatUsernameAsDisplayName(username) {
    const parts = parseGeneratedUsername(username);
    if (!parts)
        return username;
    return `${titleCase(parts.adjective)} ${titleCase(parts.noun)} ${parts.suffix}`;
}
//# sourceMappingURL=username-generator.js.map